import { useState, useEffect } from 'react';

interface CountUpOptions {
  duration?: number;
  delay?: number;
  decimals?: number;
  start?: boolean;
}

export const useCountUp = (
  endValue: number,
  { duration = 2000, delay = 0, decimals = 2, start = true }: CountUpOptions = {}
) => {
  const [count, setCount] = useState(start ? 0 : endValue);

  useEffect(() => {
    if (!start) return;

    let animationFrameId: number;
    let delayTimer: NodeJS.Timeout;

    const startCounting = () => {
      const step = (endValue - 0) / (duration / 16);
      let current = 0;
      let startTime: number | null = null;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;

        current = Math.min(current + step, endValue);
        setCount(current);

        if (progress < duration && current < endValue) {
          animationFrameId = requestAnimationFrame(animate);
        } else {
          setCount(endValue);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
    };

    if (delay > 0) {
      delayTimer = setTimeout(startCounting, delay);
    } else {
      startCounting();
    }

    return () => {
      if (delayTimer) clearTimeout(delayTimer);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [endValue, duration, delay, start]);

  const formatted = new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(count);

  return formatted.replace(/,/g, ' ');
};
